import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, MessageCircle, X, Send, Loader2, Download, FileText, FileSpreadsheet } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const AIInsightCard = ({
    title,
    insight,
    context,
    suggestions = [],
    onAskQuestion,
    className = ''
}) => {
    const [showChat, setShowChat] = useState(false);
    const [question, setQuestion] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const chatContainerRef = useRef(null);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatHistory]);

    const handleAsk = async () => {
        if (!question.trim() || loading) return;

        const userQuestion = question.trim();
        setQuestion('');
        setChatHistory(prev => [...prev, { role: 'user', content: userQuestion }]);
        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            const response = await api.post('/analytics/chat', {
                question: userQuestion,
                context,
                conversationHistory: chatHistory
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const aiMessage = response.data.response?.message ||
                'I can help you understand your analytics data. What would you like to know?';

            setChatHistory(prev => [...prev, {
                role: 'assistant',
                content: aiMessage,
                ai_enabled: response.data.response?.ai_enabled
            }]);
        } catch (error) {
            console.error('AI chat error:', error);
            setChatHistory(prev => [...prev, {
                role: 'assistant',
                content: 'Sorry, I encountered an error processing your question. Please try again.'
            }]);
        } finally {
            setLoading(false);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setQuestion(suggestion);
        setShowChat(true);
        // Auto-submit after a brief delay
        setTimeout(() => {
            const input = document.querySelector('[data-ai-input]');
            if (input) {
                input.focus();
            }
        }, 100);
    };

    const handleExport = async (reportType, format) => {
        setExportLoading(true);
        setShowExportMenu(false);

        try {
            const token = localStorage.getItem('token');
            const response = await api.post('/analytics/report', {
                reportType,
                format,
                includeAISummary: true
            }, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: format === 'csv' ? 'blob' : 'json'
            });

            if (format === 'csv') {
                // Download CSV file
                const blob = new Blob([response.data], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${reportType}-analytics-report.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
                toast.success('Report downloaded successfully');
            } else {
                // Handle JSON - could display or download
                const blob = new Blob([JSON.stringify(response.data.report, null, 2)], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${reportType}-analytics-report.json`;
                a.click();
                window.URL.revokeObjectURL(url);
                toast.success('Report downloaded successfully');
            }
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to generate report');
        } finally {
            setExportLoading(false);
        }
    };

    // Format AI message with markdown-like styling
    const formatMessage = (content) => {
        if (!content) return '';

        // Split by newlines and format
        return content.split('\n').map((line, i) => {
            // Headers
            if (line.startsWith('##')) {
                return <h4 key={i} className="font-semibold text-gray-900 mt-2 mb-1">{line.replace(/^#+\s*/, '')}</h4>;
            }
            // List items
            if (line.startsWith('- ') || line.startsWith('• ')) {
                return <li key={i} className="ml-4 list-disc">{line.replace(/^[-•]\s*/, '')}</li>;
            }
            // Bold text
            if (line.includes('**')) {
                const parts = line.split(/\*\*(.*?)\*\*/g);
                return (
                    <p key={i} className="mb-1">
                        {parts.map((part, j) =>
                            j % 2 === 1 ? <strong key={j}>{part}</strong> : part
                        )}
                    </p>
                );
            }
            // Regular text
            if (line.trim()) {
                return <p key={i} className="mb-1">{line}</p>;
            }
            return <br key={i} />;
        });
    };

    return (
        <div className={`bg-gradient-to-br from-primary/5 to-purple-50 rounded-xl border border-primary/20 overflow-hidden ${className}`}>
            {/* Header */}
            <div className="p-4 border-b border-primary/10">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-primary/10 rounded-lg">
                            <Sparkles className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900">{title || 'AI Insights'}</h3>
                            <p className="text-xs text-gray-500">Powered by AI analysis</p>
                        </div>
                    </div>

                    {/* Export Menu */}
                    <div className="relative">
                        <button
                            onClick={() => setShowExportMenu(!showExportMenu)}
                            disabled={exportLoading}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 bg-white/60 hover:bg-white rounded-lg border border-gray-200 transition-colors disabled:opacity-50"
                        >
                            {exportLoading ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                                <Download className="w-3 h-3" />
                            )}
                            Export
                        </button>

                        {showExportMenu && (
                            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                                <p className="px-3 py-1 text-xs font-medium text-gray-400 uppercase">Export Reports</p>
                                <button
                                    onClick={() => handleExport('comprehensive', 'csv')}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                    <FileSpreadsheet className="w-4 h-4" />
                                    Full Report (CSV)
                                </button>
                                <button
                                    onClick={() => handleExport('preboarding', 'csv')}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                    <FileSpreadsheet className="w-4 h-4" />
                                    Preboarding (CSV)
                                </button>
                                <button
                                    onClick={() => handleExport('onboarding', 'csv')}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                    <FileSpreadsheet className="w-4 h-4" />
                                    Onboarding (CSV)
                                </button>
                                <button
                                    onClick={() => handleExport('employees', 'csv')}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                    <FileSpreadsheet className="w-4 h-4" />
                                    Employee Report (CSV)
                                </button>
                                <button
                                    onClick={() => handleExport('compliance', 'csv')}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                    <FileSpreadsheet className="w-4 h-4" />
                                    Compliance (CSV)
                                </button>
                                <button
                                    onClick={() => handleExport('surveys', 'csv')}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                    <FileSpreadsheet className="w-4 h-4" />
                                    Surveys (CSV)
                                </button>
                                <button
                                    onClick={() => handleExport('notices', 'csv')}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                    <FileSpreadsheet className="w-4 h-4" />
                                    Notices (CSV)
                                </button>
                                <div className="border-t border-gray-100 my-1" />
                                <button
                                    onClick={() => handleExport('comprehensive', 'json')}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                    <FileText className="w-4 h-4" />
                                    Full Report (JSON)
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Insight content */}
            {insight && (
                <div className="p-4 border-b border-primary/10">
                    <p className="text-sm text-gray-700">{insight}</p>
                </div>
            )}

            {/* Suggestions */}
            {suggestions.length > 0 && !showChat && (
                <div className="p-4 space-y-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                        Ask AI about...
                    </p>
                    {suggestions.map((suggestion, index) => (
                        <button
                            key={index}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 bg-white/60 hover:bg-white rounded-lg border border-gray-200/50 hover:border-primary/30 transition-colors"
                        >
                            {suggestion}
                        </button>
                    ))}
                </div>
            )}

            {/* Chat interface */}
            {showChat && (
                <div className="flex flex-col h-96">
                    {/* Chat history */}
                    <div
                        ref={chatContainerRef}
                        className="flex-1 overflow-y-auto p-4 space-y-3"
                    >
                        {chatHistory.length === 0 && (
                            <div className="text-center py-6">
                                <Sparkles className="w-8 h-8 text-primary/40 mx-auto mb-2" />
                                <p className="text-sm text-gray-500">
                                    Ask me anything about your analytics data
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                    I can help with employee progress, task completion, compliance, and more
                                </p>
                            </div>
                        )}
                        {chatHistory.map((msg, index) => (
                            <div
                                key={index}
                                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                                <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${msg.role === 'user'
                                        ? 'bg-primary text-white'
                                        : 'bg-white border border-gray-200 text-gray-700'
                                    }`}>
                                    {msg.role === 'user' ? (
                                        msg.content
                                    ) : (
                                        <div className="space-y-1">
                                            {formatMessage(msg.content)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {loading && (
                            <div className="flex justify-start">
                                <div className="bg-white border border-gray-200 px-4 py-3 rounded-lg flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                                    <span className="text-sm text-gray-500">Analyzing your data...</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Quick suggestions in chat */}
                    {chatHistory.length > 0 && !loading && (
                        <div className="px-4 pb-2 flex flex-wrap gap-1">
                            <button
                                onClick={() => setQuestion("Show me employees who are struggling")}
                                className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-600 transition-colors"
                            >
                                Struggling employees
                            </button>
                            <button
                                onClick={() => setQuestion("What's the completion rate breakdown?")}
                                className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-600 transition-colors"
                            >
                                Completion rates
                            </button>
                            <button
                                onClick={() => setQuestion("Summarize the preboarding metrics")}
                                className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-600 transition-colors"
                            >
                                Preboarding summary
                            </button>
                        </div>
                    )}

                    {/* Input */}
                    <div className="p-3 border-t border-primary/10 bg-white/50">
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                data-ai-input
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleAsk()}
                                placeholder="Ask about your analytics..."
                                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            />
                            <button
                                onClick={handleAsk}
                                disabled={!question.trim() || loading}
                                className="p-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toggle chat button */}
            <div className="p-3 border-t border-primary/10 bg-white/30">
                <button
                    onClick={() => setShowChat(!showChat)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors"
                >
                    {showChat ? (
                        <>
                            <X className="w-4 h-4" />
                            Close Chat
                        </>
                    ) : (
                        <>
                            <MessageCircle className="w-4 h-4" />
                            Chat with AI
                        </>
                    )}
                </button>
            </div>

            {/* Click outside to close export menu */}
            {showExportMenu && (
                <div
                    className="fixed inset-0 z-0"
                    onClick={() => setShowExportMenu(false)}
                />
            )}
        </div>
    );
};

export default AIInsightCard;
